import * as yup from "yup";
import { isValidPhoneNumber } from "libphonenumber-js";

import type { PlgField, PlgOption, PlgSignupConfig, PlgStep, SignupAnswers, SignupPrefill } from "plg/types";
import { PLG_SIGNUP_ROUTE } from "constants/routes";

export const PLG_BASE_PATH = PLG_SIGNUP_ROUTE;
export const PLG_WELCOME_PATH = `${PLG_BASE_PATH}/welcome`;
/** Not a step: the step page sends a signed-in customer on to the first step after the one that created the account. */
export const PLG_SETUP_RESUME_PATH = `${PLG_BASE_PATH}/continue`;

const ARRAY_TYPES = new Set(["multiSelect", "toolPicker"]);

const toOption = (o: PlgOption) => (Array.isArray(o) ? { value: o[0], label: o[1] } : { value: o, label: o });

export function resolveOptions(field: PlgField, config: PlgSignupConfig): { value: string; label: string }[] {
  if (!field.options) return [];
  const list = typeof field.options === "string" ? config.optionLists?.[field.options] : field.options;
  return Array.isArray(list) ? list.map(toOption) : [];
}

const fieldLabel = (field: PlgField) => field.label || field.key;

export function buildStepSchema(step: PlgStep, config: PlgSignupConfig) {
  const shape: Record<string, yup.AnySchema> = {};
  step.fields.forEach((field) => {
    if (ARRAY_TYPES.has(field.type)) {
      let s = yup.array().of(yup.string());
      if (field.required) s = s.min(1, `${fieldLabel(field)} is required`);
      shape[field.key] = s;
      return;
    }
    let s = yup.string().trim();
    if (field.type === "select") {
      const values = resolveOptions(field, config).map((o) => o.value);
      s = s.test("option", `Select a valid ${fieldLabel(field).toLowerCase()}`, (v) => !v || values.includes(v));
    }
    if (field.type === "phone") {
      // PhoneInput emits "+1 6145550142"; empty stays valid unless required.
      s = s.test("phone", "Enter a valid phone number", (v) => !v || isValidPhoneNumber(v.replace(/\s+/g, "")));
    }
    if (field.required) {
      const { showWhen } = field;
      // A revealed field ("Other" → its name) is required only while it is on screen.
      s = showWhen
        ? s.when(showWhen.field, { is: showWhen.equals, then: (x) => x.required(`${fieldLabel(field)} is required`) })
        : s.required(`${fieldLabel(field)} is required`);
    }
    shape[field.key] = s;
  });
  return yup.object(shape);
}

/** "Open Dental", "open-dental" and "OpenDental" are the same tool (as the portal matches them). */
const normTool = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, "");

/** The tools in the answers, from every tool picker field. */
export function answeredTools(config: PlgSignupConfig, answers: SignupAnswers): string[] {
  return config.steps
    .flatMap((s) => s.fields)
    .filter((f) => f.type === "toolPicker")
    .flatMap((f) => (Array.isArray(answers[f.key]) ? (answers[f.key] as string[]) : []));
}

/** Whether a step is asked for these tools; the portal applies the same rule to the answers it keeps. */
export function isStepShown(step: PlgStep, tools: string[]): boolean {
  const c = step.showWhen;
  if (!c) return true;
  const picked = new Set(tools.map(normTool));
  const has = (names?: string[]) => (names || []).some((n) => picked.has(normTool(n)));
  if (c.toolsAny && !has(c.toolsAny)) return false;
  return !has(c.toolsNone);
}

/** The steps this customer is asked, in order, given their answers so far. */
export function visibleSteps(config: PlgSignupConfig, answers: SignupAnswers): PlgStep[] {
  const tools = answeredTools(config, answers);
  return config.steps.filter((s) => isStepShown(s, tools));
}

/** A field revealed by another answer is shown only while that answer holds. */
export const isFieldShown = (field: PlgField, values: SignupAnswers) =>
  !field.showWhen || values[field.showWhen.field] === field.showWhen.equals;

/** The sign-in name from Google / Microsoft (signupTokenName), for `prefill: "sso.<key>"`. */
export type SsoPrefill = { firstName?: string; lastName?: string };

function resolvePrefill(field: PlgField, prefill: SignupPrefill | null, sso: SsoPrefill): string | undefined {
  if (!field.prefill) return undefined;
  const [source, key] = field.prefill.split(".");
  const from = source === "enrichment" ? prefill : source === "sso" ? sso : null;
  const value = from && key ? (from as Record<string, unknown>)[key] : undefined;
  return typeof value === "string" ? value : undefined;
}

export function buildStepDefaults(
  step: PlgStep,
  prefill: SignupPrefill | null,
  draft: SignupAnswers,
  sso: SsoPrefill = {}
): SignupAnswers {
  const values: SignupAnswers = {};
  step.fields.forEach((field) => {
    if (draft[field.key] !== undefined) {
      values[field.key] = draft[field.key];
    } else if (ARRAY_TYPES.has(field.type)) {
      values[field.key] = [];
    } else {
      values[field.key] = resolvePrefill(field, prefill, sso) ?? "";
    }
  });
  return values;
}

/** After enrichment, empty fields stand out so the user knows where to look (prototype `.f-missing`). */
export function isFieldMissing(field: PlgField, values: SignupAnswers, enriched: boolean): boolean {
  if (!enriched) return false;
  const v = values[field.key];
  return Array.isArray(v) ? v.length === 0 : !v;
}

export function suggestedTools(config: PlgSignupConfig, industry?: string): string[] {
  const map = config.industryTools || {};
  return (industry && map[industry]) || map._default || [];
}

export function allConfigTools(config: PlgSignupConfig): string[] {
  const all = Object.values(config.industryTools || {}).flat();
  return [...new Set(all)].sort((a, b) => a.localeCompare(b));
}

/** The step before `currentKey` that these answers ask, or null on the first one. */
export function getPrevStepPath(config: PlgSignupConfig, currentKey: string, answers: SignupAnswers = {}): string | null {
  const tools = answeredTools(config, answers);
  const at = config.steps.findIndex((s) => s.key === currentKey);
  const prev = config.steps.slice(0, Math.max(at, 0)).reverse().find((s) => isStepShown(s, tools));
  return prev ? `${PLG_BASE_PATH}/${prev.key}` : null;
}

/** The step after `currentKey` (the first without one) that these answers ask, or the welcome page. */
export function getNextStepPath(config: PlgSignupConfig, currentKey?: string, answers: SignupAnswers = {}): string {
  const tools = answeredTools(config, answers);
  const from = currentKey ? config.steps.findIndex((s) => s.key === currentKey) + 1 : 0;
  const next = config.steps.slice(from).find((s) => isStepShown(s, tools));
  return next ? `${PLG_BASE_PATH}/${next.key}` : PLG_WELCOME_PATH;
}
